import { PCM_BUFFER_SIZE } from '../voice.constants';

/**
 * PCM Audio Processor for AudioWorklet
 *
 * Captures audio from MediaStream, buffers samples, converts Float32 to Int16 (PCM linear16),
 * and sends chunks via postMessage for streaming transcription.
 *
 * Buffer size is sourced from PCM_BUFFER_SIZE so the worklet and the
 * silence-padding emitter stay in sync.
 *
 * Messages:
 * - Outgoing: { type: 'audio', samples: ArrayBuffer } - PCM audio chunk
 * - Incoming: { type: 'flush' } - Send remaining buffer on stop
 */
export const PCM_PROCESSOR_CODE = `
class PCMProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.bufferSize = ${PCM_BUFFER_SIZE};
    this.buffer = new Float32Array(this.bufferSize);
    this.bufferIndex = 0;

    // Handle flush command to send remaining buffer on stop
    this.port.onmessage = (event) => {
      if (event.data.type === 'flush') {
        this.flushBuffer();
      }
    };
  }

  flushBuffer() {
    if (this.bufferIndex === 0) {
      return;
    }

    // Send partial buffer with actual sample count
    const int16Buffer = new Int16Array(this.bufferIndex);
    for (let j = 0; j < this.bufferIndex; j++) {
      const s = Math.max(-1, Math.min(1, this.buffer[j]));
      int16Buffer[j] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }

    this.port.postMessage({
      type: 'audio',
      samples: int16Buffer.buffer,
    }, [int16Buffer.buffer]);

    this.bufferIndex = 0;
  }

  process(inputs) {
    const input = inputs[0];
    if (!input || !input[0]) {
      return true;
    }

    const samples = input[0];

    for (let i = 0; i < samples.length; i++) {
      this.buffer[this.bufferIndex++] = samples[i];

      if (this.bufferIndex >= this.bufferSize) {
        const int16Buffer = new Int16Array(this.bufferSize);
        for (let j = 0; j < this.bufferSize; j++) {
          const s = Math.max(-1, Math.min(1, this.buffer[j]));
          int16Buffer[j] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }

        this.port.postMessage({
          type: 'audio',
          samples: int16Buffer.buffer,
        }, [int16Buffer.buffer]);

        this.bufferIndex = 0;
      }
    }

    return true;
  }
}

registerProcessor('pcm-processor', PCMProcessor);
`;
